import { tierOf, type AuthorityTier } from './identity';
import { hasMinimumRole, hasPermission, type Permission } from './permissions';
import { ROLES, type Role } from './roles';

export interface RbacSubject {
  role?: string | null;
}

export interface RbacPolicy {
  permission?: Permission;
  minimumRole?: Role;
  roles?: readonly Role[];
  authorityTiers?: readonly AuthorityTier[];
}

const VALID_ROLES = new Set<string>(Object.values(ROLES));

function roleOf(subject: RbacSubject | Role | string | null | undefined): Role | undefined {
  const candidate = typeof subject === 'string' ? subject : subject?.role;
  if (candidate === 'department_admin') return 'admin';
  return candidate && VALID_ROLES.has(candidate) ? (candidate as Role) : undefined;
}

/**
 * The single client-side RBAC rendering decision. It reads only the canonical
 * backend role and permission helpers; claimed tiers/flags are ignored. This
 * controls presentation only — every route and mutation remains server guarded.
 */
export function isRbacAllowed(
  subject: RbacSubject | Role | string | null | undefined,
  policy: RbacPolicy = {},
): boolean {
  const constrained =
    !!policy.permission || !!policy.minimumRole || !!policy.roles || !!policy.authorityTiers;
  if (!constrained) return true;

  const role = roleOf(subject);
  if (!role) return false;
  if (policy.permission && !hasPermission(role, policy.permission)) return false;
  if (policy.minimumRole && !hasMinimumRole(role, policy.minimumRole)) return false;
  if (policy.roles && !policy.roles.includes(role)
    && !(role === ROLES.DEPARTMENT_ADMIN && policy.roles.includes(ROLES.ADMIN))) return false;
  if (policy.authorityTiers && !policy.authorityTiers.includes(tierOf(role))) return false;
  return true;
}

export function rbacVisibleItems<T extends RbacPolicy>(
  items: readonly T[],
  subject: RbacSubject | Role | string | null | undefined,
): T[] {
  return items.filter((item) => isRbacAllowed(subject, item));
}
